import type { ITransformRT2 } from '@seddi/math-v2';
import { Vector2, Vector3 } from '@seddi/three';

import type { VertexDataDesc } from '../core/bufferTypes';
import type { TriMeshGrid2 } from './triMeshGrid2';

type Basis = { normal: Vector3; position: Vector3; tangent: Vector3 };

/**
 *
 * @param data - Vertices data.
 * @param grid - Triangle mesh grid.
 * @param transform - 2D transform.
 * @param ref - Target 3D basis.
 */
export const computeTransformBasis = (
  data: VertexDataDesc,
  grid: TriMeshGrid2,
  transform: ITransformRT2,
  ref: Basis,
) => {
  const { indices, normals, positions, uvs } = data;
  const { translation } = transform;
  // Find triangle coordinates.
  const { triangle, coordinates: ts } = grid.query(translation);
  // Interpolate basis.
  const offset = triangle * 3;
  const i0 = indices[offset];
  const i1 = indices[offset + 1];
  const i2 = indices[offset + 2];
  interpolateValue3(positions, i0, i1, i2, ts.x, ts.y, ts.z, ref.position);
  interpolateValue3(normals!, i0, i1, i2, ts.x, ts.y, ts.z, ref.normal);
  computeTriangleTangentFromUvs(positions, uvs!, i0, i1, i2, ref.tangent);
  // TODO: rotate basis?
};

/**
 * Get Vector2 element from vertices data.
 * @param data - Vertices data.
 * @param i - Index.
 * @param ref - Ref.
 */
const atToRef2 = (data: Float32Array, i: number, ref: Vector2) => {
  const o = i * 2;
  ref.set(data[o], data[o + 1]);
};

/**
 * Get Vector3 element from vertices data.
 * @param data - Vertices data.
 * @param i - Index.
 * @param ref - Ref.
 */
const atToRef3 = (data: Float32Array, i: number, ref: Vector3) => {
  const o = i * 3;
  ref.set(data[o], data[o + 1], data[o + 2]);
};

/**
 * Compute triangle tangent from uvs.
 * @param positions - Vertex data positions.
 * @param uvs - Vertex data uvs.
 * @param i0 - Triangle index 0.
 * @param i1 - Triangle index 1.
 * @param i2 - Triangle index 2.
 * @param ref - Tangent output.
 */
const computeTriangleTangentFromUvs = (
  positions: Float32Array,
  uvs: Float32Array,
  i0: number,
  i1: number,
  i2: number,
  ref: Vector3,
) => {
  const p0 = _v30;
  const p1 = _v31;
  const p2 = _v32;
  const uv0 = _v20;
  const uv1 = _v21;
  const uv2 = _v22;
  atToRef3(positions, i0, p0);
  atToRef3(positions, i1, p1);
  atToRef3(positions, i2, p2);
  atToRef2(uvs, i0, uv0);
  atToRef2(uvs, i1, uv1);
  atToRef2(uvs, i2, uv2);

  const edge1 = _v33;
  const edge2 = _v34;
  const edgeUv1 = _v23;
  const edgeUv2 = _v24;
  edge1.subVectors(p1, p0);
  edge2.subVectors(p2, p0);
  edgeUv1.subVectors(uv1, uv0);
  edgeUv2.subVectors(uv2, uv0);

  const f = 1.0 / (edgeUv1.x * edgeUv2.y - edgeUv2.x * edgeUv1.y || 1);
  const x = f * (edgeUv2.y * edge1.x - edgeUv1.y * edge2.x);
  const y = f * (edgeUv2.y * edge1.y - edgeUv1.y * edge2.y);
  const z = f * (edgeUv2.y * edge1.z - edgeUv1.y * edge2.z);

  ref.set(x, y, z).normalize();
};

/**
 * Interpolate triangle mesh value.
 * @param data - Vertex data.
 * @param i0 - Triangle index 0.
 * @param i1 - Triangle index 1.
 * @param i2 - Triangle index 2.
 * @param t0 - Barycentric coordinates 0.
 * @param t1 - Barycentric coordinates 1.
 * @param t2 - Barycentric coordinates 2.
 * @param target - Value output.
 */
const interpolateValue3 = (
  data: Float32Array,
  i0: number,
  i1: number,
  i2: number,
  t0: number,
  t1: number,
  t2: number,
  target: Vector3,
) => {
  const v0 = _v30;
  const v1 = _v31;
  const v2 = _v32;
  atToRef3(data, i0, v0);
  atToRef3(data, i1, v1);
  atToRef3(data, i2, v2);
  target
    .set(0, 0, 0)
    .add(v0.multiplyScalar(t0))
    .add(v1.multiplyScalar(t1))
    .add(v2.multiplyScalar(t2));
};

const _v20 = new Vector2();
const _v21 = new Vector2();
const _v22 = new Vector2();
const _v23 = new Vector2();
const _v24 = new Vector2();
const _v30 = new Vector3();
const _v31 = new Vector3();
const _v32 = new Vector3();
const _v33 = new Vector3();
const _v34 = new Vector3();
